import React, { useState, useEffect, useRef, useCallback } from 'react';
import '../Css/PaymentReminders.css';
import '@tabler/icons-webfont/dist/tabler-icons.min.css';
import Navbar from './Navbar';
import { Link } from 'react-router-dom';
import PaginationControls from './Pagination';

const ITEMS_PER_PAGE = 20;

const EMPTY_SUMMARY = {
    totalBills: 0,
    totalOutstanding: 0,
    overdueCount: 0,
    overdueAmount: 0,
    dueTodayCount: 0,
    dueTodayAmount: 0,
    upcomingCount: 0,
    upcomingAmount: 0,
    needDueDateCount: 0,
};

const TEMPLATES = {
    friendly: `Dear {{Customer Name}},

Gentle reminder — invoice {{Invoice Number}} of ₹{{Amount Due}} is due on {{Due Date}}.

Pay securely here: {{Payment Link}}

Thank you,
{{Business Name}}`,
    firm: `Dear {{Customer Name}},

Invoice {{Invoice Number}} of ₹{{Amount Due}} is now overdue ({{Due Date}}). Please clear it at the earliest to avoid supply hold.

Pay now: {{Payment Link}}

{{Business Name}} · {{Support Number}}`,
    final: `Dear {{Customer Name}},

FINAL NOTICE — invoice {{Invoice Number}} of ₹{{Amount Due}} remains unpaid since {{Due Date}}. Kindly settle immediately.

{{Payment Link}}

{{Business Name}}`
};

const VARS = ['Customer Name', 'Invoice Number', 'Amount Due', 'Due Date', 'Payment Link', 'Bill Image Link', 'Business Name', 'Support Number', 'QR Code'];

const RULE_LABELS = {
    before3: '3 days before due',
    onDue: 'on due date',
    every7: 'every 7 days until paid',
    over3: '3 days overdue',
    over15: '15 days overdue'
};

const PaymentReminders = () => {
    const adminEmail = sessionStorage.getItem('userEmail') || '';
    const today = new Date();

    const dISO = (off) => {
        const d = new Date(today);
        d.setDate(d.getDate() + off);
        return d.toISOString().slice(0, 10);
    };

    const [bills, setBills] = useState([]);
    const [totalBills, setTotalBills] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [summary, setSummary] = useState(EMPTY_SUMMARY);
    const [routeOptions, setRouteOptions] = useState([]);
    const [repOptions, setRepOptions] = useState([]);
    const [loadingBills, setLoadingBills] = useState(true);
    const [loadingSummary, setLoadingSummary] = useState(true);
    const [billsError, setBillsError] = useState('');
    const [orgSettings, setOrgSettings] = useState({
        senderName: '',
        supportNumber: '',
        paymentBaseUrl: 'pay.zordigo.com',
    });
    const [source, setSource] = useState('ledger');
    const [overdueOnly, setOverdueOnly] = useState(false);
    const [selected, setSelected] = useState(new Map());
    const [channels, setChannels] = useState(new Set(['whatsapp', 'email']));
    const [rules, setRules] = useState(new Set(['before3', 'onDue', 'every7']));
    const [podEnabled, setPodEnabled] = useState(true);
    const [payLink, setPayLink] = useState(true);
    const [payQr, setPayQr] = useState(true);
    const [attachInvoice, setAttachInvoice] = useState(true);
    const [attachStatement, setAttachStatement] = useState(false);
    const [smartLimits, setSmartLimits] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [routeFilter, setRouteFilter] = useState('');
    const [repFilter, setRepFilter] = useState('');
    const [manualSeq, setManualSeq] = useState(0);
    const [template, setTemplate] = useState('friendly');
    const [templateText, setTemplateText] = useState(TEMPLATES.friendly);
    const [sendTime, setSendTime] = useState('2:00 PM');
    const [toasts, setToasts] = useState([]);
    const [modal, setModal] = useState(null);
    const [todayPayments, setTodayPayments] = useState(null);
    const fileInputRefs = useRef({});

    const [manualForm, setManualForm] = useState({
        customer: '', invoice: '', amount: '', due: '', phone: '', email: ''
    });

    const inr = (n) => (n == null ? '—' : '₹' + Math.round(n).toLocaleString('en-IN'));
    const inrK = (n) => {
        const a = Math.abs(n);
        if (a >= 100000) return '₹' + (n / 100000).toFixed(2) + 'L';
        if (a >= 1000) return '₹' + (n / 1000).toFixed(1) + 'k';
        return '₹' + Math.round(n);
    };

    const ageDays = (due) => {
        if (!due) return null;
        return Math.floor((today - new Date(due)) / 86400000);
    };

    const fmtDate = (iso) => {
        if (!iso) return '—';
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '—';
        return d.getDate() + ' ' + ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][d.getMonth()];
    };

    const showToast = (msg, kind = 'ok') => {
        const id = Date.now();
        setToasts((prev) => [...prev, { id, msg, kind }]);
        setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 2400);
    };

    const loadSummary = useCallback(async () => {
        if (!adminEmail) return;
        setLoadingSummary(true);
        try {
            const res = await fetch(`/api/outstanding-invoices/summary?adminEmail=${encodeURIComponent(adminEmail)}`);
            if (!res.ok) return;
            const json = await res.json();
            if (json.summary) setSummary(json.summary);
            if (json.filterOptions) {
                setRouteOptions(json.filterOptions.routes || []);
                setRepOptions(json.filterOptions.reps || []);
            }
        } catch (e) {
            /* keep previous summary */
        } finally {
            setLoadingSummary(false);
        }
    }, [adminEmail]);

    const loadInvoicesFromApi = useCallback(async (page = 1) => {
        setLoadingBills(true);
        setBillsError('');
        try {
            const params = new URLSearchParams({
                page: String(page),
                limit: String(ITEMS_PER_PAGE),
            });
            if (adminEmail) params.set('adminEmail', adminEmail);
            if (debouncedSearch) params.set('customer', debouncedSearch);
            if (routeFilter) params.set('route', routeFilter);
            if (repFilter) params.set('rep', repFilter);
            if (overdueOnly) params.set('overdueOnly', 'true');

            const res = await fetch(`/api/outstanding-invoices?${params.toString()}`);
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.error || `Could not load invoices (${res.status})`);
            }
            const json = await res.json();
            setBills(Array.isArray(json.data) ? json.data : []);
            setTotalBills(json.total ?? 0);
            setCurrentPage(json.page ?? page);
        } catch (e) {
            setBills([]);
            setTotalBills(0);
            const msg = e.message || 'Failed to load outstanding invoices';
            setBillsError(msg);
            showToast(msg, 'bad');
        } finally {
            setLoadingBills(false);
        }
    }, [adminEmail, debouncedSearch, routeFilter, repFilter, overdueOnly]);

    const loadOrgSettings = useCallback(async () => {
        if (!adminEmail) return;
        try {
            const res = await fetch(`/api/reminder-settings?adminEmail=${encodeURIComponent(adminEmail)}`);
            if (!res.ok) return;
            const data = await res.json();
            if (data) {
                setOrgSettings({
                    senderName: data.senderName || '',
                    supportNumber: data.supportNumber || '',
                    paymentBaseUrl: data.paymentBaseUrl || 'pay.zordigo.com',
                });
            }
        } catch (e) {
            /* keep defaults */
        }
    }, [adminEmail]);

    const loadTodayPayments = useCallback(async () => {
        if (!adminEmail) return;
        try {
            const res = await fetch(
                `/api/reminder-payments/today?adminEmail=${encodeURIComponent(adminEmail)}`
            );
            if (!res.ok) return;
            const data = await res.json();
            if (!data?.count) return;

            const dismissKey = `pr-today-payments-${data.date || ''}`;
            if (sessionStorage.getItem(dismissKey) === '1') {
                setTodayPayments(data);
                return;
            }
            setTodayPayments(data);
            setModal({ type: 'todayPayments', data });
        } catch (e) {
            /* ignore */
        }
    }, [adminEmail]);

    useEffect(() => {
        loadSummary();
        loadOrgSettings();
        loadTodayPayments();
    }, [loadSummary, loadOrgSettings, loadTodayPayments]);

    const dismissTodayPayments = () => {
        if (todayPayments?.date) {
            sessionStorage.setItem(`pr-today-payments-${todayPayments.date}`, '1');
        }
        setModal(null);
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(searchQuery.trim());
            setCurrentPage(1);
        }, 350);
        return () => clearTimeout(timer);
    }, [searchQuery]);

    useEffect(() => {
        if (source !== 'ledger') return;
        loadInvoicesFromApi(currentPage);
    }, [source, currentPage, loadInvoicesFromApi]);

    const handlePageChange = (page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleSalesmanToggle = () => {
        setModal({ type: 'comingSoon' });
    };

    const routes = routeOptions;
    const reps = repOptions;

    const ageBadge = (due) => {
        const a = ageDays(due);
        if (a == null) return <span className="pr-age">No due date</span>;
        if (a < 0) return <span className="pr-age cur">{-a}d to due</span>;
        if (a === 0) return <span className="pr-age w30">Due today</span>;
        if (a <= 30) return <span className="pr-age w30">{a}d over</span>;
        if (a <= 60) return <span className="pr-age w60">{a}d over</span>;
        return <span className="pr-age w90">{a}d over</span>;
    };

    const updateBillDueDate = async (id, due, isManual = false) => {
        const patch = { due, dueDateMissing: !due };
        setBills((prev) => prev.map((b) => (b.id === id ? { ...b, ...patch } : b)));
        setSelected((prev) => {
            const next = new Map(prev);
            const bill = next.get(id);
            if (bill) next.set(id, { ...bill, ...patch });
            return next;
        });

        if (!due || isManual) return;

        try {
            const qs = adminEmail ? `?adminEmail=${encodeURIComponent(adminEmail)}` : '';
            const res = await fetch(`/api/reminder-orders/${id}/due-date${qs}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ due_date: due }),
            });
            if (!res.ok) throw new Error('Could not save due date');
            showToast('Due date saved on order');
            loadSummary();
        } catch (e) {
            showToast('Due date set for this reminder only (could not save to order)', 'bad');
        }
    };

    const toggleBill = (bill) => {
        setSelected((prev) => {
            const next = new Map(prev);
            if (next.has(bill.id)) next.delete(bill.id);
            else next.set(bill.id, bill);
            return next;
        });
    };

    const toggleSelectAll = () => {
        const allSel = bills.length && bills.every((b) => selected.has(b.id));
        setSelected((prev) => {
            const next = new Map(prev);
            if (allSel) bills.forEach((b) => next.delete(b.id));
            else bills.forEach((b) => next.set(b.id, b));
            return next;
        });
    };

    const selectAllOutstanding = () => {
        setSelected((prev) => {
            const next = new Map(prev);
            bills.filter((b) => { const a = ageDays(b.due); return a != null && a > 0; }).forEach((b) => next.set(b.id, b));
            return next;
        });
    };

    const addManual = () => {
        const { customer, invoice, amount, due, phone, email } = manualForm;
        const amt = parseFloat(amount) || 0;
        if (!customer || !amt) {
            showToast('Enter at least a customer name and amount', 'bad');
            return;
        }
        const id = 'm' + (manualSeq + 1);
        setManualSeq((s) => s + 1);
        const bill = {
            id, customer, invoice: invoice || ('MANUAL-' + (manualSeq + 1)), amount: amt,
            due: due || dISO(0), route: 'Manual', rep: '—', phone, email, manual: true
        };
        setBills((prev) => [...prev, bill]);
        setSelected((prev) => new Map(prev).set(id, bill));
        setManualForm({ customer: '', invoice: '', amount: '', due: '', phone: '', email: '' });
        showToast('Bill added to the list');
    };

    const removeBill = (id) => {
        setSelected((prev) => {
            const next = new Map(prev);
            next.delete(id);
            return next;
        });
    };

    const clearBasket = () => setSelected(new Map());

    const attachPod = (id, file) => {
        if (!file || file.size > 8 * 1024 * 1024) {
            showToast('Image is over 8 MB — please use a smaller photo', 'bad');
            return;
        }
        const reader = new FileReader();
        reader.onload = (e) => {
            setSelected((prev) => {
                const next = new Map(prev);
                const b = next.get(id);
                if (b) next.set(id, { ...b, pod: e.target.result, podName: file.name });
                return next;
            });
            showToast('Signed bill attached');
        };
        reader.readAsDataURL(file);
    };

    const removePod = (id) => {
        setSelected((prev) => {
            const next = new Map(prev);
            const b = next.get(id);
            if (b) {
                const copy = { ...b };
                delete copy.pod;
                delete copy.podName;
                next.set(id, copy);
            }
            return next;
        });
        setModal(null);
        showToast('Signed bill removed');
    };

    const toggleChannel = (ch) => {
        setChannels((prev) => {
            const next = new Set(prev);
            if (next.has(ch)) {
                if (next.size > 1) next.delete(ch);
            } else next.add(ch);
            return next;
        });
    };

    const toggleRule = (rule) => {
        setRules((prev) => {
            const next = new Set(prev);
            if (next.has(rule)) {
                if (next.size > 1) next.delete(rule);
            } else next.add(rule);
            return next;
        });
    };

    const insertVar = (v) => setTemplateText((t) => t + `{{${v}}}`);

    const sampleBill = () => {
        const items = [...selected.values()];
        return items[0] || bills[0] || { customer: 'Customer', invoice: 'INV-0000', amount: 0, due: dISO(0), rep: '' };
    };

    const renderPreviewHtml = () => {
        const sample = sampleBill();
        const payBase = (orgSettings.paymentBaseUrl || 'pay.zordigo.com').replace(/\/$/, '');
        const map = {
            'Customer Name': sample.customer,
            'Invoice Number': sample.invoice,
            'Amount Due': Math.round(sample.amount).toLocaleString('en-IN'),
            'Due Date': fmtDate(sample.due),
            'Payment Link': `${payBase}/${(sample.invoice || 'INV').replace(/\s+/g, '')}`,
            'Bill Image Link': attachInvoice ? `[latest bill for ${sample.orderNo || sample.invoice || 'INV'}]` : '—',
            'Business Name': orgSettings.senderName || 'Your business',
            'Support Number': orgSettings.supportNumber || '—',
            'QR Code': '[QR]'
        };
        let txt = templateText;
        VARS.forEach((v) => {
            txt = txt.split(`{{${v}}}`).join(`\0${map[v] ?? v}\0`);
        });
        return txt.split('\0').map((part, i) => {
            if (i % 2 === 1) return <span key={i} className="pr-v">{part}</span>;
            return part;
        });
    };

    const firstReminderText = () => {
        if (rules.has('before3')) return '3 days before each due date';
        if (rules.has('onDue')) return 'on each due date';
        if (rules.size) return RULE_LABELS[[...rules][0]];
        return '—';
    };

    const items = [...selected.values()];
    const total = items.reduce((s, b) => s + b.amount, 0);
    const podCount = items.filter((b) => b.pod).length;
    const canLaunch = items.length > 0 && channels.size > 0 && rules.size > 0;
    const allSel = bills.length && bills.every((b) => selected.has(b.id));
    const pageStart = totalBills === 0 ? 0 : (currentPage - 1) * ITEMS_PER_PAGE + 1;
    const pageEnd = Math.min(currentPage * ITEMS_PER_PAGE, totalBills);

    const launch = async () => {
        if (!canLaunch) return;
        const missingDue = items.filter((b) => !b.due);
        if (missingDue.length) {
            showToast(`Add a next payment date for ${missingDue.length} selected bill(s)`, 'bad');
            return;
        }
        const scheduleRules = Object.fromEntries([...rules].map((r) => [r, true]));
        const payload = {
            name: 'Reminder ' + today.toLocaleDateString('en-IN'),
            status: 'scheduled',
            channels: [...channels],
            scheduleRules,
            sendTime,
            notifyRetailer: true,
            notifySalesman: false,
            retailerBody: templateText,
            salesmanBody: '',
            attachSignedBill: podEnabled,
            attachInvoice: !!attachInvoice,
            includePaymentLink: !!payLink,
            bills: items.map((b) => ({
                customer: b.customer, invoice: b.invoice, amount: b.amount, due: b.due,
                orderNo: b.orderNo || b.invoice || null,
                partyId: b.partyId || null, ledgerEntryId: b.ledgerEntryId || b.id || null,
                salesmanId: b.salesmanId || null, rep: b.rep || null,
                phone: b.phone || null, email: b.email || null, manual: !!b.manual,
                podUrl: b.pod ? ('uploaded://' + (b.podName || 'signed-bill')) : null
            }))
        };

        let saved = false;
        try {
            const url = adminEmail
                ? `/api/reminder-campaigns?adminEmail=${encodeURIComponent(adminEmail)}`
                : '/api/reminder-campaigns';
            const res = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            if (res.ok) saved = true;
        } catch (e) {
            /* offline demo */
        }

        setModal({ type: 'launch', items, total, saved, podCount });
    };

    const renderKPIs = () => {
        const s = summary;
        const kpis = [
            { label: 'Total outstanding', icon: 'ti-wallet', val: loadingSummary ? '…' : inrK(s.totalOutstanding), foot: `${s.totalBills} pending bills` },
            { label: 'Overdue', icon: 'ti-alarm', cls: 'bad', val: loadingSummary ? '…' : inrK(s.overdueAmount), foot: `${s.overdueCount} bills` },
            { label: 'Due today', icon: 'ti-clock', cls: 'warn', val: loadingSummary ? '…' : inrK(s.dueTodayAmount), foot: `${s.dueTodayCount} bills` },
            { label: 'Upcoming', icon: 'ti-calendar', val: loadingSummary ? '…' : inrK(s.upcomingAmount), foot: `${s.upcomingCount} bills` },
            { label: 'Need due date', icon: 'ti-calendar-plus', cls: s.needDueDateCount ? 'warn' : '', val: loadingSummary ? '…' : String(s.needDueDateCount), foot: 'set before scheduling' },
            { label: 'Selected', icon: 'ti-checkbox', val: String(selected.size), foot: 'across all pages' },
        ];
        return kpis.map((k) => (
            <div key={k.label} className="pr-kpi">
                <div className="pr-kpi-label"><i className={`ti ${k.icon}`}></i> {k.label}</div>
                <div className={`pr-kpi-value ${k.cls || ''} tnum`}>{k.val}</div>
                <div className="pr-kpi-foot">{k.foot}</div>
            </div>
        ));
    };

    const chLabel = (c) => ({ whatsapp: 'WhatsApp', email: 'Email', sms: 'SMS' }[c]);

    return (
        <div className="pr-page">
            <Navbar />

            <div className="pr-page-actions">
                {todayPayments?.count > 0 && (
                    <button
                        type="button"
                        className="pr-page-btn"
                        onClick={() => setModal({ type: 'todayPayments', data: todayPayments })}
                    >
                        <i className="ti ti-currency-rupee"></i>
                        Payments today ({todayPayments.count})
                    </button>
                )}
                <Link to="/PaymentSettings?tab=history" className="pr-page-btn"><i className="ti ti-history"></i> Reminder history</Link>
                <Link to="/PaymentSettings" className="pr-page-btn"><i className="ti ti-settings"></i> Settings</Link>
            </div>

            <div className="pr-kpis">{renderKPIs()}</div>

            <div className="pr-wrap">
                <div className="pr-col">
                    <div className="pr-panel">
                        <div className="pr-panel-head">
                            <div>
                                <div className="pr-panel-title"><span className="pr-step">1</span> Add bills to remind</div>
                                <div className="pr-panel-sub">Same orders as <b>Pending Amount</b> — partial payments with balance due.</div>
                            </div>
                            <div className="pr-seg">
                                <button type="button" className={source === 'ledger' ? 'active' : ''} onClick={() => setSource('ledger')}><i className="ti ti-database"></i> Pending Amount</button>
                                <button type="button" className={source === 'manual' ? 'active' : ''} onClick={() => setSource('manual')}><i className="ti ti-pencil-plus"></i> Add manually</button>
                            </div>
                        </div>

                        {source === 'ledger' ? (
                            <div className="pr-panel-body">
                                <div className="pr-filters">
                                    <div className="pr-search"><i className="ti ti-search"></i><input placeholder="Search customer or invoice no…" value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} /></div>
                                    <select className="pr-selectbox" value={routeFilter} onChange={(e) => { setRouteFilter(e.target.value); setCurrentPage(1); }}>
                                        <option value="">All routes</option>
                                        {routes.map((r) => <option key={r} value={r}>{r}</option>)}
                                    </select>
                                    {/* <select className="pr-selectbox" value={repFilter} onChange={(e) => { setRepFilter(e.target.value); setCurrentPage(1); }}>
                                        <option value="">All salesmen</option>
                                        {reps.map((r) => <option key={r} value={r}>{r}</option>)}
                                    </select> */}
                                    <div className={`pr-chip-toggle ${overdueOnly ? 'on' : ''}`} onClick={() => { setOverdueOnly(!overdueOnly); setCurrentPage(1); }}><i className="ti ti-alarm"></i> Overdue only</div>
                                </div>
                                <div className="pr-bills">
                                    <div className="pr-bills-head">
                                        <div onClick={toggleSelectAll} style={{ cursor: 'pointer' }}><span className={`pr-ck ${allSel ? 'on' : ''}`}><i className="ti ti-check"></i></span></div>
                                        <div>Customer / Invoice</div>
                                        <div style={{ textAlign: 'right' }}>Outstanding</div>
                                        <div>Due date</div>
                                        <div>Age</div>
                                    </div>
                                    {loadingBills ? (
                                        <div className="pr-empty"><i className="ti ti-loader"></i>Loading pending bills…</div>
                                    ) : bills.length ? bills.map((b) => (
                                        <div key={b.id} className={`pr-bill-row ${selected.has(b.id) ? 'sel' : ''}`} onClick={() => toggleBill(b)}>
                                            <span className="pr-ck"><i className="ti ti-check"></i></span>
                                            <div>
                                                <div className="pr-bill-cust truncate">{b.customer}</div>
                                                <div className="pr-bill-meta tnum">{b.invoice} · {b.route}</div>
                                            </div>
                                            <div className="pr-bill-amt tnum">{inr(b.amount)}</div>
                                            <div className="pr-bill-due tnum" onClick={(e) => e.stopPropagation()}>
                                                {b.due ? fmtDate(b.due) : (
                                                    <input
                                                        type="date"
                                                        className="pr-due-input"
                                                        title="Set next payment date"
                                                        onChange={(e) => updateBillDueDate(b.id, e.target.value, b.manual)}
                                                    />
                                                )}
                                            </div>
                                            <div>{ageBadge(b.amount)}</div>
                                        </div>
                                    )) : (
                                        <div className="pr-empty">
                                            <i className="ti ti-file-search"></i>
                                            {billsError
                                                ? billsError
                                                : ((debouncedSearch || routeFilter || repFilter || overdueOnly)
                                                    ? 'No bills match these filters.'
                                                    : 'No pending amount orders found.')}
                                        </div>
                                    )}
                                    <div className="pr-bills-foot">
                                        <span className="tnum">
                                            {totalBills > 0
                                                ? `Showing ${pageStart}–${pageEnd} of ${totalBills.toLocaleString('en-IN')}`
                                                : 'No matching bills'}
                                            {selected.size > 0 && ` · ${selected.size} selected`}
                                        </span>
                                        <button type="button" className="pr-link" onClick={selectAllOutstanding}>Select overdue on page</button>
                                    </div>
                                </div>
                                {totalBills > ITEMS_PER_PAGE && (
                                    <div className="pr-pagination">
                                        <PaginationControls
                                            totalItems={totalBills}
                                            itemsPerPage={ITEMS_PER_PAGE}
                                            currentPage={currentPage}
                                            onPageChange={handlePageChange}
                                        />
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div className="pr-panel-body">
                                <div className="pr-form-grid">
                                    <div className="pr-fld"><label>Customer name</label><input placeholder="e.g. Sharma Stores" value={manualForm.customer} onChange={(e) => setManualForm({ ...manualForm, customer: e.target.value })} /></div>
                                    <div className="pr-fld"><label>Invoice number</label><input placeholder="INV-2526-0150" value={manualForm.invoice} onChange={(e) => setManualForm({ ...manualForm, invoice: e.target.value })} /></div>
                                    <div className="pr-fld"><label>Outstanding amount (₹)</label><input type="number" placeholder="15000" value={manualForm.amount} onChange={(e) => setManualForm({ ...manualForm, amount: e.target.value })} /></div>
                                    <div className="pr-fld"><label>Due date</label><input type="date" value={manualForm.due} onChange={(e) => setManualForm({ ...manualForm, due: e.target.value })} /></div>
                                    <div className="pr-fld"><label>Phone / WhatsApp</label><input placeholder="+91 …" value={manualForm.phone} onChange={(e) => setManualForm({ ...manualForm, phone: e.target.value })} /></div>
                                    <div className="pr-fld"><label>Email (optional)</label><input placeholder="name@store.com" value={manualForm.email} onChange={(e) => setManualForm({ ...manualForm, email: e.target.value })} /></div>
                                </div>
                                <button type="button" className="pr-btn ghost" style={{ marginTop: 12 }} onClick={addManual}><i className="ti ti-plus"></i> Add this bill to the list</button>
                            </div>
                        )}
                    </div>

                    <div className="pr-panel">
                        <div className="pr-panel-head">
                            <div className="pr-panel-title"><span className="pr-step">2</span> Selected bills</div>
                            {items.length > 0 && <button type="button" className="pr-link" onClick={clearBasket}>Clear all</button>}
                        </div>
                        <div className="pr-panel-body">
                            {!items.length ? (
                                <div className="pr-empty"><i className="ti ti-inbox"></i>No bills selected yet. Pick from the ledger or add one manually.</div>
                            ) : items.map((b) => (
                                <div key={b.id} className="pr-basket-item">
                                    {podEnabled && (
                                        b.pod ? (
                                            <div className="pr-pod-thumb" title="Signed bill attached — click to view" onClick={() => setModal({ type: 'pod', bill: b })}>
                                                <img src={b.pod} alt="signed bill" />
                                                <span className="pr-pod-tick"><i className="ti ti-check"></i></span>
                                            </div>
                                        ) : (
                                            <label className="pr-pod-add" title="Attach signed bill photo">
                                                <i className="ti ti-camera-plus"></i>
                                                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={(e) => attachPod(b.id, e.target.files[0])} />
                                            </label>
                                        )
                                    )}
                                    <div className="pr-basket-meta">
                                        <div className="pr-basket-name truncate">
                                            {b.customer} {b.manual && <span className="pr-age w30" style={{ marginLeft: 4 }}>manual</span>}
                                        </div>
                                        <div className="pr-basket-sub tnum">
                                            {b.invoice}
                                            {b.due ? ` · due ${fmtDate(b.due)}` : ' · '}
                                            {!b.due && (
                                                <input
                                                    type="date"
                                                    className="pr-due-input inline"
                                                    title="Set next payment date"
                                                    onChange={(e) => updateBillDueDate(b.id, e.target.value)}
                                                />
                                            )}
                                            {b.due && ' · '}{ageBadge(b.due)}
                                        </div>
                                    </div>
                                    <div className="pr-basket-amt tnum">{inr(b.amount)}</div>
                                    <button type="button" className="pr-x-btn" onClick={() => removeBill(b.id)}><i className="ti ti-x"></i></button>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="pr-col">
                    <div className="pr-panel">
                        <div className="pr-panel-head">
                            <div className="pr-panel-title"><span className="pr-step">3</span> How to remind</div>
                        </div>

                        <div className="pr-cfg-block">
                            <div className="pr-cfg-label">Channels</div>
                            <div className="pr-channels">
                                {['whatsapp', 'email', 'sms'].map((ch) => (
                                    <div key={ch} className={`pr-channel ${channels.has(ch) ? 'on' : ''}`} onClick={() => toggleChannel(ch)}>
                                        {/* <div className="pr-tick">{channels.has(ch) && <i className="ti ti-check"></i>}</div> */}
                                        <i className={`ti ti-${ch === 'whatsapp' ? 'brand-whatsapp' : (ch === 'email' ? 'mail' : 'message')}`}></i>
                                        <div className="pr-channel-name">{chLabel(ch)}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <div className="pr-cfg-block">
                            <div className="pr-cfg-label">Who gets the reminder</div>
                            <div className="pr-switch-row">
                                <div><div className="pr-lbl"><i className="ti ti-building-store" style={{ fontSize: 13, verticalAlign: -1, color: 'var(--ink-3)' }}></i> Retailer / customer</div><div className="pr-sub">The dunning message with payment link</div></div>
                                <div className="pr-sw on locked" title="Always on"></div>
                            </div>
                            <div className="pr-switch-row">
                                <div>
                                    <div className="pr-lbl">
                                        <i className="ti ti-user-star" style={{ fontSize: 13, verticalAlign: -1, color: 'var(--ink-3)' }}></i> Also send to salesman
                                        <span className="pr-soon-badge">Soon</span>
                                    </div>
                                    <div className="pr-sub">A field follow-up alert in their own format</div>
                                </div>
                                <div className="pr-sw locked" onClick={handleSalesmanToggle} title="Coming in the next update"></div>
                            </div>
                        </div>

                        <div className="pr-cfg-block">
                            <div className="pr-cfg-label">When to send</div>
                            {[
                                { key: 'before3', txt: '3 days before due date' },
                                { key: 'onDue', txt: 'on the due date' },
                                { key: 'every7', txt: 'Every 7 days until paid', sub: 'Stops automatically when the bill is cleared', recommend: true },
                                { key: 'over3', txt: '3 days overdue' },
                                { key: 'over15', txt: '15 days overdue' },
                            ].map((r) => (
                                <div key={r.key} className={`pr-rule ${rules.has(r.key) ? 'on' : ''} ${r.recommend ? 'recommend' : ''}`} onClick={() => toggleRule(r.key)}>
                                    <span className="pr-box">{rules.has(r.key) && <i className="ti ti-check"></i>}</span>
                                    <div><div className="pr-rule-txt">{r.txt}</div>{r.sub && <div className="pr-rule-sub">{r.sub}</div>}</div>
                                </div>
                            ))}
                            <div className="pr-switch-row" style={{ marginTop: 6 }}>
                                <div><div className="pr-lbl">Preferred send time</div></div>
                                <select className="pr-selectbox" value={sendTime} onChange={(e) => setSendTime(e.target.value)}>
                                    {['9:00 AM', '11:00 AM', '2:00 PM', '6:00 PM'].map((t) => <option key={t}>{t}</option>)}
                                </select>
                            </div>
                        </div>

                        <div className="pr-cfg-block">
                            <div className="pr-cfg-label">Message</div>
                            <select className="pr-selectbox" style={{ width: '100%', marginBottom: 9 }} value={template} onChange={(e) => { setTemplate(e.target.value); setTemplateText(TEMPLATES[e.target.value]); }}>
                                <option value="friendly">Friendly reminder</option>
                                <option value="firm">Firm — overdue</option>
                                <option value="final">Final notice</option>
                            </select>
                            <textarea className="pr-tmpl-textarea" value={templateText} onChange={(e) => setTemplateText(e.target.value)} />
                            <div className="pr-var-row">
                                {VARS.map((v) => <span key={v} className="pr-var" onClick={() => insertVar(v)}>{`{{${v}}}`}</span>)}
                            </div>
                            <div className="pr-preview">{renderPreviewHtml()}</div>
                        </div>

                        <div className="pr-cfg-block">
                            <div className="pr-cfg-label">Make it easy to pay</div>
                            <div className="pr-switch-row"><div><div className="pr-lbl">Attach secure payment link</div><div className="pr-sub">Uses this organization&apos;s UPI so payment goes to their account</div></div><div className={`pr-sw ${payLink ? 'on' : ''}`} onClick={() => setPayLink(!payLink)}></div></div>
                            <div className="pr-switch-row"><div><div className="pr-lbl">Include payment QR code</div><div className="pr-sub">Scannable inside WhatsApp &amp; email</div></div><div className={`pr-sw ${payQr ? 'on' : ''}`} onClick={() => setPayQr(!payQr)}></div></div>
                            <div className="pr-attach-grid" style={{ marginTop: 8 }}>
                                <button type="button" className={`pr-attach ${attachInvoice ? 'on' : ''}`} onClick={() => setAttachInvoice(!attachInvoice)}><i className="ti ti-file-invoice"></i> Latest bill PDF / image</button>
                                <button type="button" className={`pr-attach ${attachStatement ? 'on' : ''}`} onClick={() => setAttachStatement(!attachStatement)}><i className="ti ti-report-money"></i> Statement</button>
                            </div>
                            <div className="pr-switch-row" style={{ marginTop: 10, borderTop: '1px solid var(--line)', paddingTop: 11 }}>
                                <div><div className="pr-lbl"><i className="ti ti-signature" style={{ fontSize: 13, verticalAlign: -1, color: 'var(--ink-3)' }}></i> Attach signed bill (Proof of delivery)</div><div className="pr-sub">The receiver&apos;s signed copy goes with each reminder</div></div>
                                <div className={`pr-sw ${podEnabled ? 'on' : ''}`} onClick={() => setPodEnabled(!podEnabled)}></div>
                            </div>
                            {podEnabled && (
                                <div className="pr-pod-hint">
                                    <i className="ti ti-info-circle"></i>
                                    <span>Upload each bill&apos;s signed photo from the <b>Selected bills</b> list on the left. Bills without an image still send — just without the proof.</span>
                                </div>
                            )}
                        </div>

                        <div className="pr-cfg-block">
                            <div className="pr-cfg-label">Smart limits &amp; escalation</div>
                            <div className="pr-switch-row"><div><div className="pr-lbl">Don&apos;t over-message</div><div className="pr-sub">Max 1 reminder/day · 3/week</div></div><div className={`pr-sw ${smartLimits ? 'on' : ''}`} onClick={() => setSmartLimits(!smartLimits)}></div></div>
                            <div style={{ marginTop: 6 }}>
                                <div className="pr-esc-step"><span className="pr-esc-dot" style={{ background: 'var(--info)' }}></span> 3 reminders ignored → notify salesperson</div>
                                <div className="pr-esc-step"><span className="pr-esc-dot" style={{ background: 'var(--warn)' }}></span> 7 ignored → notify sales manager</div>
                                <div className="pr-esc-step"><span className="pr-esc-dot" style={{ background: 'var(--bad)' }}></span> 15 days overdue → notify owner</div>
                            </div>
                        </div>
                    </div>

                    <div className="pr-panel pr-launch">
                        <div className="pr-panel-body">
                            <div className="pr-summary-row"><span>Bills selected</span><b>{items.length}</b></div>
                            <div className="pr-summary-row"><span>Total outstanding</span><b className="tnum">{inr(total)}</b></div>
                            <div className="pr-summary-row"><span>Channels</span><b>{channels.size ? [...channels].map(chLabel).join(' + ') : '—'}</b></div>
                            <div className="pr-summary-row"><span>Recipients</span><b>Retailer</b></div>
                            {podEnabled && podCount > 0 && (
                                <div className="pr-summary-row"><span>Signed bills attached</span><b>{podCount} of {items.length} bills</b></div>
                            )}
                            <div className="pr-summary-row"><span>First reminder</span><b>{firstReminderText()}</b></div>
                            <button type="button" className="pr-btn primary" style={{ marginTop: 11 }} disabled={!canLaunch} onClick={launch}><i className="ti ti-send"></i> Schedule reminders</button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="pr-toast-wrap">
                {toasts.map((t) => (
                    <div key={t.id} className={`pr-toast ${t.kind === 'bad' ? 'bad' : ''}`}>
                        <i className={`ti ${t.kind === 'bad' ? 'ti-alert-triangle' : 'ti-check'}`}></i> {t.msg}
                    </div>
                ))}
            </div>

            {modal?.type === 'launch' && (
                <div className="pr-modal-bg" onClick={() => setModal(null)}>
                    <div className="pr-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="pr-modal-top">
                            <div className="pr-modal-icon"><i className="ti ti-circle-check"></i></div>
                            <div className="pr-modal-title">Reminders scheduled</div>
                            <div className="pr-modal-msg">
                                {modal.items.length} bill{modal.items.length !== 1 ? 's' : ''} worth <b>{inr(modal.total)}</b> will be chased automatically. Reminders stop the moment a bill is paid.
                                {!modal.saved && <span style={{ color: 'var(--ink-3)' }}> (could not save — check server connection)</span>}
                            </div>
                            <div className="pr-modal-list">
                                <div className="pr-summary-row"><span>Channels</span><b>{[...channels].map(chLabel).join(', ')}</b></div>
                                <div className="pr-summary-row"><span>Goes to</span><b>Retailer</b></div>
                                <div className="pr-summary-row"><span>Schedule</span><b>{[...rules].map((r) => RULE_LABELS[r]).join(', ')}</b></div>
                                <div className="pr-summary-row"><span>Send time</span><b>{sendTime}</b></div>
                                <div className="pr-summary-row"><span>Payment link</span><b>Attached</b></div>
                                {podEnabled && modal.podCount > 0 && (
                                    <div className="pr-summary-row"><span>Signed bill (POD)</span><b>{modal.podCount} attached</b></div>
                                )}
                            </div>
                        </div>
                        <div className="pr-modal-foot">
                            <button type="button" className="pr-btn ghost" onClick={() => setModal(null)}>Close</button>
                            <button type="button" className="pr-btn primary" onClick={() => { setModal(null); clearBasket(); }}><i className="ti ti-check"></i> Done</button>
                        </div>
                    </div>
                </div>
            )}

            {modal?.type === 'todayPayments' && modal.data && (
                <div className="pr-modal-bg" onClick={dismissTodayPayments}>
                    <div className="pr-modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
                        <div className="pr-modal-top" style={{ textAlign: 'left' }}>
                            <div className="pr-modal-icon" style={{ background: 'var(--ok-bg)', color: 'var(--ok)', margin: '0 auto 10px' }}>
                                <i className="ti ti-currency-rupee"></i>
                            </div>
                            <div className="pr-modal-title" style={{ textAlign: 'center' }}>Payments received today</div>
                            <div className="pr-modal-msg" style={{ textAlign: 'center' }}>
                                {modal.data.count} payment{modal.data.count !== 1 ? 's' : ''} for orders due today
                                {modal.data.totalAmount != null ? (
                                    <> · <b>{inr(modal.data.totalAmount)}</b></>
                                ) : null}
                            </div>
                            <div className="pr-pay-list">
                                {(modal.data.payments || []).map((p) => (
                                    <div key={p.paymentId} className="pr-pay-row">
                                        <div className="pr-pay-main">
                                            <div className="pr-pay-customer truncate">{p.customer || 'Customer'}</div>
                                            <div className="pr-pay-meta">
                                                {p.invoice || '—'}
                                                {p.amount != null ? ` · ${inr(p.amount)}` : ''}
                                            </div>
                                        </div>
                                        <div className="pr-pay-id" title="Payment ID for cross-verification">
                                            {p.paymentId}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                        <div className="pr-modal-foot">
                            <Link to="/PaymentSettings?tab=history" className="pr-btn ghost" onClick={dismissTodayPayments}>
                                View history
                            </Link>
                            <button type="button" className="pr-btn primary" onClick={dismissTodayPayments}>Got it</button>
                        </div>
                    </div>
                </div>
            )}

            {modal?.type === 'comingSoon' && (
                <div className="pr-modal-bg" onClick={() => setModal(null)}>
                    <div className="pr-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="pr-modal-top">
                            <div className="pr-modal-icon" style={{ background: 'var(--info-bg)', color: 'var(--info)' }}>
                                <i className="ti ti-clock-hour-4"></i>
                            </div>
                            <div className="pr-modal-title">Salesman alerts — coming soon</div>
                            <div className="pr-modal-msg">
                                Sending follow-up alerts to salesmen isn&apos;t wired up yet. Retailer reminders work today — salesman notifications will ship in the next update.
                            </div>
                        </div>
                        <div className="pr-modal-foot">
                            <button type="button" className="pr-btn primary" onClick={() => setModal(null)}>Got it</button>
                        </div>
                    </div>
                </div>
            )}

            {modal?.type === 'pod' && modal.bill && (
                <div className="pr-modal-bg" onClick={() => setModal(null)}>
                    <div className="pr-modal" style={{ maxWidth: 380 }} onClick={(e) => e.stopPropagation()}>
                        <div style={{ padding: '14px 16px', borderBottom: '1px solid var(--line)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                            <div className="pr-panel-title" style={{ fontSize: 13 }}><i className="ti ti-signature"></i> Signed bill · {modal.bill.customer}</div>
                            <button type="button" className="pr-x-btn" onClick={() => setModal(null)}><i className="ti ti-x"></i></button>
                        </div>
                        <div style={{ padding: 14, textAlign: 'center', background: 'var(--surface-2)' }}>
                            <img src={modal.bill.pod} alt="signed bill" style={{ maxWidth: '100%', maxHeight: 420, borderRadius: 8, border: '1px solid var(--line)' }} />
                            <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 8 }}>{modal.bill.invoice} · this image will be sent with the reminder</div>
                        </div>
                        <div className="pr-modal-foot" style={{ padding: '14px 16px' }}>
                            <button type="button" className="pr-btn ghost" onClick={() => removePod(modal.bill.id)}><i className="ti ti-trash"></i> Remove</button>
                            <button type="button" className="pr-btn primary" onClick={() => setModal(null)}>Keep</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PaymentReminders;
